import { atom } from 'jotai'

import {
  isPositiveNumber,
  maxLength,
  required,
  validateField
} from '@sequencehq/utils/validation'
import { IntegrationServices } from '@sequencehq/api'

import { getRateValidators, quantityValidators } from '../../common/validation'
import { formStateAtom, SubItem } from './state.atoms'
import { filterPlaceholderSubItem } from './fields'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import {
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  LINE_ITEM_NAME_MAX_LENGTH
} from '../../constants'

type ValidationResults = {
  title: string[]
  description: string[]
  quantity: string[]
  rate: string[]
  subItems: Array<{
    title: string[]
    rate: string[]
    quantity: string[]
  }>
  discount: {
    title: string[]
    amount: string[]
  }
  taxCategoryId: string[]
  externalIds: Array<{
    key: IntegrationServices
    value: string[]
  }>
}

export const validationResultsAtom = atom<ValidationResults | null>(null)

export const validateFormAtom = atom(null, (get, set) => {
  const state = get(formStateAtom)

  const rateValidators = getRateValidators(state.rateDisplay)

  const subItems = filterPlaceholderSubItem(state.subItems)

  const results = {
    title: validateField(state.title)([
      required,
      maxLength(
        LINE_ITEM_NAME_MAX_LENGTH,
        `Name must not exceed ${LINE_ITEM_NAME_MAX_LENGTH} characters`
      )
    ]),
    description: validateField(state.description)([
      maxLength(
        LINE_ITEM_DESCRIPTION_MAX_LENGTH,
        `Description must not exceed ${LINE_ITEM_DESCRIPTION_MAX_LENGTH} characters`
      )
    ]),
    ...(state.subItemsEnabled
      ? {
          quantity: [],
          rate: [],
          subItems: subItems.map((subItem: SubItem) => ({
            title: validateField(subItem.title)([required]),
            quantity: validateField(subItem.quantity)(quantityValidators),
            rate: validateField(subItem.rate)(rateValidators)
          }))
        }
      : {
          quantity: validateField(state.quantity)(quantityValidators),
          rate: validateField(state.rate)(rateValidators),
          subItems: []
        }),
    ...(state.discountEnabled
      ? {
          discount: {
            title: validateField(state.discount.title)([required]),
            amount: validateField(state.discount.amount)([
              required,
              isPositiveNumber
            ])
          }
        }
      : { discount: { title: [], amount: [] } }),
    taxCategoryId: validateField(state.taxCategoryId)([required]),
    externalIds: state.externalIds.map(externalId => ({
      key: externalId.key,
      value: validateField(externalId.value)([required])
    }))
  }

  const secondaryOptionsValid = isSecondaryOptionsValid(results)

  if (!secondaryOptionsValid) {
    set(secondaryOptionsVisibleAtom, true)
  }

  set(validationResultsAtom, results)
  return isFormValid(results)
})

const isSecondaryOptionsValid = (results: ValidationResults): boolean => {
  const { discount, externalIds } = results

  const discountValid = Object.values(discount).every(
    errors => errors.length === 0
  )

  const externalIdsValid = externalIds.every(
    externalId => externalId.value.length === 0
  )

  return [discountValid, externalIdsValid].every(Boolean)
}

const isFormValid = (results: ValidationResults): boolean => {
  const { subItems, discount, externalIds, ...baseFields } = results

  const baseFieldsValid = Object.values(baseFields).every(
    errors => errors.length === 0
  )

  const subItemsValid = subItems.every(subItemValidation =>
    Object.values(subItemValidation).every(errors => errors.length === 0)
  )

  const secondaryOptionsValid = isSecondaryOptionsValid(results)

  return [baseFieldsValid, subItemsValid, secondaryOptionsValid].every(Boolean)
}
